<template>
    <v-container class="fill-height data-container" fluid>

        <v-form ref="siteForm" :lazy-validation="true" v-model="formValid">

            <v-stepper class="stepper" v-model="currentStep">
                <v-stepper-header style="height: 50px;">
                    <v-stepper-step class="stepper-step" step="1" :complete="currentStep > 1" editable :color="'#c42111'">Site Data</v-stepper-step>
                    <v-divider style="margin: 0px -6px;"></v-divider>
                    <v-stepper-step class="stepper-step" step="2" :complete="currentStep > 2" editable :color="'#c42111'">Geographic Location</v-stepper-step>
                    <v-divider style="margin: 0px -6px;"></v-divider>
                    <v-stepper-step class="stepper-step" step="3" :complete="currentStep > 3" editable :color="'#c42111'">Sectors</v-stepper-step>
                    <v-divider style="margin: 0px -6px;"></v-divider>
                    <v-stepper-step class="stepper-step" step="4" :complete="currentStep > 4" editable :color="'#c42111'">Site Book</v-stepper-step>
                </v-stepper-header>

                <v-stepper-items class="stepper-content-container">
                    <v-stepper-content :step="1" class="stepper-content">

                        <div class="flex-container">
                            <div style="margin-right: 20px;">
                                <site-image :site="$props.site" :disabled="$props.lockSave"></site-image>
                            </div>

                            <div style="flex-grow: 1;">
                                <v-row>
                                    <v-col cols="4" md="2">
                                        <v-checkbox label="Alias to:" v-model="aliasChecked" @change="aliasCheckChange" color="#c42111"></v-checkbox>
                                    </v-col>
                                    <v-col cols="8" md="10">
                                        <v-autocomplete label="Alias" v-model="$props.site.site_alias" color="#c42111" :items="aliasList" :disabled="!aliasChecked" :rules="[validation.aliasRequired(aliasChecked, $props.site.site_alias)]" />
                                    </v-col>

                                    <v-col cols="12" md="6" xl="4">
                                        <v-text-field label="Site Name" color="#c42111" v-model="$props.site.site_name" :disabled="aliasChecked" />
                                    </v-col>
                                    <v-col cols="12" md="6" xl="4">
                                        <v-combobox label="Locale" hint="Press 'RETURN' key to add new" persistent-hint v-model="$props.site.site_locale" color="#c42111" :items="localeList" :disabled="aliasChecked" />
                                    </v-col>
                                    <v-col cols="12" md="6" xl="4">
                                        <v-text-field label="Office" v-model="$props.site.site_office" color="#c42111" :disabled="$props.site.site_id != 0" :rules="[validation.officeOrLocationRequired($props.site.site_office, $props.site.site_location)]" />
                                    </v-col>

                                    <v-col cols="12" md="6" xl="4">
                                        <v-text-field label="Location" v-model="$props.site.site_location" color="#c42111" :disabled="$props.site.site_id != 0" :rules="[validation.officeOrLocationRequired($props.site.site_office, $props.site.site_location)]" />
                                    </v-col>
                                    <v-col cols="12" md="6" xl="4">
                                        <v-select label="Technician" v-model="$props.site.user_id" color="#c42111" :items="technicianList" />
                                    </v-col>
                                    <v-col cols="12" md="6" xl="4">
                                        <v-select label="Threshold Profile" v-model="$props.site.tp_id" color="#c42111" :items="thresholdProfileList" />
                                    </v-col>
                                </v-row>
                            </div>
                        </div>
                        
                        <v-row>
                            <v-col cols="12" md="6" xl="8">
                                <v-combobox label="Site Tags" v-model="$props.site.site_groups" multiple color="#c42111" :items="groupList" :search-input.sync="siteTagSearch" />
                            </v-col>

                            <v-col cols="12" md="6" xl="4">
                                <v-select label="Tree Structure" v-model="$props.site.tree_id" color="#c42111" :items="treeList" :disabled="aliasChecked" @change="changedTreeStructure" />
                            </v-col>

                            <v-col cols="12" md="6" xl="4">
                                <v-select label="Network Category" v-model="$props.site.network_category_id" color="#c42111" :items="networkCategories" item-text="network_category_name" item-value="network_category_id">
                                    <template v-slot:selection="{item}">
                                        <v-icon class="mr-2 color-selected" :color="item.network_category_color">{{ item.network_category_icon }}</v-icon>
                                        <span class="mt-2" v-text="item.network_category_name" />
                                    </template>
                                    <template v-slot:item="{item}">
                                        <v-icon class="mr-2 color-selected" :color="item.network_category_color">{{ item.network_category_icon }}</v-icon>
                                        <span class="mt-2" v-text="item.network_category_name" />
                                    </template>
                                </v-select>
                            </v-col>

                            <v-col cols="12" md="6" xl="4">
                                <v-menu v-model="editMaintStartTime" :close-on-content-click="false" transition="scale-transition" offset-y min-width="290px">
                                    <template v-slot:activator="{ on }">
                                        <v-text-field v-model="$props.site.maint_start" color="#c42111" label="Maintenance Window Start Time" prepend-icon="access_time" readonly v-on="on"></v-text-field>
                                    </template>
                                    <v-time-picker v-model="$props.site.maint_start" color="#c42111" :hourOnly="false" :format="'24hr'" use-seconds scrollable />
                                </v-menu>
                            </v-col>
                            <v-col cols="12" md="6" xl="4">
                                <v-menu v-model="editMaintEndTime" :close-on-content-click="false" transition="scale-transition" offset-y min-width="290px">
                                    <template v-slot:activator="{ on }">
                                        <v-text-field v-model="$props.site.maint_end" color="#c42111" label="Maintenance Window End Time" prepend-icon="access_time" readonly v-on="on"></v-text-field>
                                    </template>
                                    <v-time-picker v-model="$props.site.maint_end" color="#c42111" :hourOnly="false" :format="'24hr'" use-seconds scrollable />
                                </v-menu>
                            </v-col>
                        </v-row>
                    </v-stepper-content>

                    <v-stepper-content :step="2" class="stepper-content">
                        <div class="flex-container">
                        <div style="flex-grow: 1;">
                            <v-row>
                                <v-col cols="12">
                                    <v-radio-group v-model="geoType" row :disabled="aliasChecked">
                                        <v-radio label="Point" value="point" color="#c42111"></v-radio>
                                        <v-radio label="Link" value="link" color="#c42111"></v-radio>
                                    </v-radio-group>
                                </v-col>
                                <v-col cols="12" md="12" xl="6">
                                    <v-text-field label="Latitude" type="number" suffix="deg" v-model="$props.site.site_lat" color="#c42111" :disabled="geoType == 'link' || aliasChecked" @change="debounceSetStaticMapImgUrl">
                                        <template v-slot:append-outer>
                                            <v-hover v-slot:default="{ hover }">
                                                <v-icon :color="hover ? '#c42111' : '#ffffff'" @click="showDmsModal = true; dmsLat = true;" :disabled="geoType == 'link' || aliasChecked">mdi-latitude</v-icon>
                                            </v-hover>
                                        </template>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" md="12" xl="6">
                                    <v-text-field label="Longitude" type="number" suffix="deg" v-model="$props.site.site_long" color="#c42111" :disabled="geoType == 'link' || aliasChecked" @change="debounceSetStaticMapImgUrl">
                                        <template v-slot:append-outer>
                                            <v-hover v-slot:default="{ hover }">
                                                <v-icon :color="hover ? '#c42111' : '#ffffff'" @click="showDmsModal = true; dmsLat = false;" :disabled="geoType == 'link' || aliasChecked">mdi-longitude</v-icon>
                                            </v-hover>
                                        </template>
                                        </v-text-field>
                                </v-col>
                                <v-col cols="12" md="12" xl="6">
                                    <v-autocomplete label="End Point 1" v-model="endPointOne" color="#c42111" :items="endPointList" @change="geoSiteOneChanged" :disabled="geoType == 'point' || aliasChecked" />
                                </v-col>
                                <v-col cols="12" md="12" xl="6">
                                    <v-autocomplete label="End Point 2" v-model="endPointTwo" color="#c42111" :items="endPointList" @change="geoSiteTwoChanged" :disabled="geoType == 'point' || aliasChecked" />
                                </v-col>
                            </v-row>
                        </div>

                            <v-row>
                            <v-col cols="12" md="12" xl="12">
                                <div class="map-container" :class="{empty: !staticMapImgUrl}">
                                    <img :src="staticMapImgUrl" v-if="staticMapImgUrl" />
                                    <div class="empty-map-container" v-if="!staticMapImgUrl">
                                        <v-icon class="no-map-image-icon">mdi-map-marker-off</v-icon>
                                        <span class="no-map-text">No Map Image Available.</span>
                                    </div>
                                </div>
                            </v-col>
                            </v-row>

                        </div>
                    </v-stepper-content>

                    <v-stepper-content :step="3" class="stepper-content">
                        <sectors :siteName="$props.site.site_name || `${$props.site.site_office} - ${$props.site.site_location}`" :sectors="$props.site.sectors" :lockSave="$props.lockSave"></sectors>
                    </v-stepper-content>

                    <v-stepper-content :step="4" class="stepper-content">
                        <site-data-repo :site="$props.site" :lockSave="$props.lockSave" v-if="$props.site.tree_id != -1 && !gettingSitebook" />
                        <span class="no-tree-set" v-else>Please Assign a Site Tree Structure</span>
                    </v-stepper-content>

                </v-stepper-items>
            </v-stepper>

            <v-dialog v-model="showDmsModal" max-width="60%" width="300px" persistent>
                <dms-convert @close="showDmsModal = false" :lat="dmsLat" v-if="showDmsModal" @set="dmsSet"></dms-convert>
            </v-dialog>

            <v-dialog v-model="showSectors" max-width="80%" width="1600px" persistent>
                <sectors :siteName="$props.site.site_name || `${$props.site.site_office} - ${$props.site.site_location}`" :sectors="$props.site.sectors" :lockSave="$props.lockSave" v-if="showSectors" @close="showSectors = false"></sectors>
            </v-dialog>
            
            <v-btn class="mt-3 mr-3" color="#c42111" @click="saveClicked" :disabled="$props.lockSave">
                Save
                <v-tooltip bottom color="rgba(48, 48, 48, 1.0)" v-if="$store.state.isDirty">
                    <template v-slot:activator="{ on }">
                        <v-icon class="is-dirty-save-button-icon" v-on="on">mdi-information-outline</v-icon>
                    </template>
                    <span>Pending Changes</span>
                </v-tooltip>
            </v-btn>

            <v-btn class="mt-3 mr-3" color="#c42111" @click="cancelClicked">Cancel</v-btn>
            
        
        </v-form>
    </v-container>
</template>

<script>
import _ from 'lodash';
import Sectors from "@/components/Sites/Sectors";
import DmsConvert from "@/components/Sites/DmsConvert";
import SiteImage from "@/components/Sites/SiteImage";
import SiteDataRepo from "@/components/Sites/SiteDataRepo";
export default {
    name: 'SitesEdit',
    props: ['site', 'siteResources', 'lockSave'],
    components: {
        Sectors,
        DmsConvert,
        SiteImage,
        SiteDataRepo
    },

    data: () => ({
        currentStep: 1,
        formValid: true,
        aliasChecked: false,
        aliasList: [],
        localeList: [],
        technicianList: [],
        thresholdProfileList: [],
        treeList: [],
        groupList: [],
        mapList: [],
        endPointList: [],
        geoType: 'point',
        endPointOne: "[none]",
        endPointTwo: "[none]",
        showDmsModal: false,
        dmsLat: true,
        showSectors: false,
        editMaintStartTime: false,
        editMaintEndTime: false,
        networkCategories: [],
        validation: {
            aliasRequired: (alias, value) => (!alias || (value != -1 && value != null) ? true : false) || `Site alias is required`,
            officeOrLocationRequired: (office, location) => ((office || location) ? true : false) || 'Office and Location cannot both be empty'
        },
        siteTagSearch: null,

        staticMapImgUrl: null,
        gettingSitebook: false,
        ready: false
    }),

    computed: {

    },

    methods: {
        createResourceLists: function() {
            for(let locale of this.$props.siteResources.distinct_office_locales) {
                if(!this.localeList.includes(locale.site_locale) && locale.site_locale != '[Unassigned]') {
                    this.localeList.push(locale.site_locale);
                }
            }

            this.technicianList.push({text: '[none]', value: -1});
            for(let user of this.$props.siteResources.users_list) {
                this.technicianList.push({
                    text: user.user_fullname,
                    value: user.user_id
                });
            }

            this.thresholdProfileList.push({text: '[none]', value: -1});
            for(let tp of this.$props.siteResources.threshold_profiles) {
                this.thresholdProfileList.push({
                    text: tp.tp_name,
                    value: tp.tp_id
                });
            }

            this.treeList.push({text: '[none]', value: -1});
            for(let tree of this.$props.siteResources.admin_site_book_trees) {
                this.treeList.push({
                    text: tree.tree_name,
                    value: tree.tree_id
                });
            }

            for(let group of this.$props.siteResources.admin_site_groups) {
                this.groupList.push(group.site_group_name);
            }

            for(let site of this.$props.siteResources.sites) {
                this.aliasList.push({
                    text: `${site.site_office}/${site.site_location}/${site.site_name}`,
                    value: site.site_id
                });
            }
            if(this.$props.site.site_alias != -1) {
                this.aliasChecked = true;
            }

            this.endPointList.push('[none]');
            for(let geoSite of this.$props.siteResources.geo_sites) {
                this.endPointList.push(geoSite.geo_site);
            }
            if(this.$props.site.site_lat2 > -999) {
                this.geoType = 'link';
                for(let geoSite of this.$props.siteResources.geo_sites) {
                    if((geoSite.geo_lat == this.$props.site.site_lat) && (geoSite.geo_long == this.$props.site.site_long)) {
                        this.endPointOne = geoSite.geo_site;
                    }
                    if((geoSite.geo_lat == this.$props.site.site_lat2) && (geoSite.geo_long == this.$props.site.site_long2)) {
                        this.endPointTwo = geoSite.geo_site;
                    }
                }
            }

            this.networkCategories = this.$props.siteResources.network_categories;
            this.networkCategories.unshift({network_category_name: '[none]', network_category_id: -1, network_category_color: null, network_category_icon: null});

            // Format site tags(site groups)
            let selectedTagNames = [];
            for(let groupId of this.$props.site.site_groups) {
                for(let groupObj of this.$props.siteResources.admin_site_groups) {
                    if(groupId == groupObj.site_group_id) {
                        selectedTagNames.push(groupObj.site_group_name);
                        break;
                    }
                }
            }
            this.$props.site.site_groups = selectedTagNames;
        },

        aliasCheckChange: function(val) {
            if(!val) {
                this.$props.site.site_alias = -1
            }
        },

        geoSiteOneChanged: function(val) {
            for(let geoSite of this.$props.siteResources.geo_sites) {
                if(val == geoSite.geo_site) {
                    this.$props.site.site_lat = geoSite.geo_lat;
                    this.$props.site.site_long = geoSite.geo_long;
                    this.debounceSetStaticMapImgUrl();
                }
            }
        },

        geoSiteTwoChanged: function(val) {
            for(let geoSite of this.$props.siteResources.geo_sites) {
                if(val == geoSite.geo_site) {
                    this.$props.site.site_lat2 = geoSite.geo_lat;
                    this.$props.site.site_long2 = geoSite.geo_long;
                    this.debounceSetStaticMapImgUrl();
                }
            }
        },

        dmsSet: function(val) {
            if(this.dmsLat) {
                this.$props.site.site_lat = val;
            }
            else {
                this.$props.site.site_long = val;
            }
            this.showDmsModal = false;
        },

        saveClicked: function() {
            if(this.$refs.siteForm.validate()) {
                // Check for duplicate (office,location)s Only if its a new site.
                if(this.$props.site.site_id == 0 && this.$props.siteResources.sites.some(s => s.site_office == this.$props.site.site_office && s.site_location == this.$props.site.site_location)) {
                    this.$store.commit('showConfirmModal', {
                        title: 'Duplicate Site',
                        message: `The sites list already contains a site with this office(<font color="#FB8C00"><b>${this.$props.site.site_office}</b></font>) and location(<font color="#FB8C00"><b>${this.$props.site.site_location}</b></font>)`,
                        type: 'warning',
                        hideCancel: true,
                        confirmAction: function(){}
                    });
                    return;
                }

                // If the "geoType" is "point" then we need to make sure that site_lat2 and site_long2 are both "-1000" to prevent the site from being set as a "link"
                if(this.geoType == "point") {
                    this.$props.site.site_lat2 = -1000;
                    this.$props.site.site_long2 = -1000;
                }

                this.$emit('save', this.$props.site);
            }
        },

        cancelClicked: function() {
            if(this.$store.state.isDirty) {
                this.$store.commit('showConfirmModal', {
                    title: 'Are you sure?',
                    message: 'Are you sure you want to abandon your changes without saving?',
                    type: 'warning',
                    confirmAction: () => {this.$store.commit('setIsDirty', false); this.$emit('cancel');}
                });
            }
            else {
                this.$emit('cancel');
            }
        },

        setStaticMapImgUrl: function() {
            let url = "";
            if(this.geoType == "point" && this.$props.site.site_lat && this.$props.site.site_long && !this.aliasChecked && this.$props.site.site_lat != -1000 && this.$props.site.site_long != -1000) {
                let apiKey = 'AIzaSyCT5eLcVMKyPSicy_5iEn8onYno7aHnAgQ';
                let lat = this.$props.site.site_lat;
                let lng = this.$props.site.site_long;
                let center = `${lat},${lng}`;
                let mapType = 'roadmap';
                let zoom = '13';
                let size = '640x640';
                let scale = '2';
                //let markerColor = 'red';
                let markerColor = 'c42111';
                let label = "";
                url = `https://maps.googleapis.com/maps/api/staticmap?center=${center}&zoom=${zoom}&size=${size}&scale=${scale}&maptype=${mapType}&markers=color:${markerColor}%7Clabel:${label}%7C${lat},${lng}&key=${apiKey}`;
            }
            else if(this.geoType == "link" && !this.aliasChecked && this.$props.site.site_lat && this.$props.site.site_long && this.$props.site.site_lat2 && this.$props.site.site_long2) {
                let apiKey = 'AIzaSyCT5eLcVMKyPSicy_5iEn8onYno7aHnAgQ';
                let lat = this.$props.site.site_lat;
                let lng = this.$props.site.site_long;
                let lat2 = this.$props.site.site_lat2;
                let lng2 = this.$props.site.site_long2;

                //let center = `${lat},${lng}`;
                let mapType = 'roadmap';
                let zoom = '9';
                let size = '640x640';
                let scale = '2';

                url = `https://maps.googleapis.com/maps/api/staticmap?zoom=${zoom}&size=${size}&scale=${scale}&maptype=${mapType}&path=color:0xff0000ff|weight:5|${lat},${lng}|${lat2},${lng2}&key=${apiKey}`;
            }
            this.staticMapImgUrl = url;
        },

        debounceSetStaticMapImgUrl: _.debounce(function() {
            this.setStaticMapImgUrl();
        }, 3000),

        changedTreeStructure: function() {
            if(this.$props.site.tree_id == -1) {
                return;
            }
            let route = (TELSA.apiHost + "sitebook/tree");
            TELSA.loading(true);
            this.gettingSitebook = true;
            return this.$http.post(route, {args: {site: this.$props.site}}).then(response => {
                let result = response.body;
                if(!result.tree.length) {
                    result.tree = [this.$props.siteResources.admin_site_book_trees.find(t => t.tree_id == this.$props.site.tree_id)];

                }
                this.$props.site.sitebook = result;
                this.$props.site.sitebook.dataRepoStructure = null;
                this.gettingSitebook = false;
                TELSA.loading(false);
            });
        }
    },

    created() {
        this.createResourceLists();
    },

    mounted() {
        this.setStaticMapImgUrl();
        this.$nextTick(() => {
            this.ready = true;
        });
    },

    watch: {
        site: {
            handler(newVal, oldVal) {
                if(!this.$props.lockSave && this.ready) {
                    this.$store.commit('setIsDirty', true);
                }
            },
            deep: true
        }
    },


};
</script>

<style scoped>

    .stepper {
        background: #424242; 
        height: calc(100vh - 126px);
    }
    .stepper .stepper-step {
        padding: 12px 24px;
    }
    .stepper-content-container {
        overflow-y: auto;
        height: calc(100% - 55px);
    }
    .stepper-content {
        /* padding: 16px; */
    }

    .data-container {
        padding: 0;
    }

    .flex-container {
        flex-grow: 1;
        display: flex;
        width: 100%;
    }
    @media (max-width: 959px) {
        .flex-container {
            flex-grow: 1;
            display: block;
        }
    }


    .map-container {
        max-height: calc(100vh - 245px);
        position: relative;
    }
    .map-container.empty {
        height: 100%;
    }
    .map-container img {
        height: 100%;
        width: 100%;
    }

    @media (min-width: 960px) {
        .map-container {
            margin-left: 20px;
        }
    }

    .empty-map-container {
        position: relative;
        height: 100%;
        width: 100%;
        max-height: 100%;
        max-width: 100%;
        border: solid 2px #303030;
        color: #181818;
        text-align: center;
        min-width: 450px;
        min-height: 180px;
    }
    .empty-map-container .no-map-image-icon {
        color: rgba(0, 0, 0, 0.6);
        font-size: 130px;
        position: relative;
        position: absolute;
        top: calc(50% - (170px / 2));
        left: calc(50% - (130px / 2));
    }
    .empty-map-container .no-map-text {
        position: relative;
        position: absolute;
        top: calc(50% + 60px - (36px / 2));
        left: calc(50% - (268px / 2));
        display: block;
        font-size: 24px;
        color: rgba(0, 0, 0, 0.6);
        font-weight: bold;
    }

    .no-tree-set {
        display: block;
        text-align: center;
        font-size: 22px;
        color: rgba(255, 255, 255, 0.5);
        margin-top: calc(50vh - 160px);
    }

</style>
